import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import useAxios from '../../../hooks/useAxios';
import { SettingsContext } from '../../../store/SettingsContext';
import { UserContext } from '../../../store/UserContext';
import DialogAdaptive from '../../common/DialogAdaptive';
import DialogContentAdaptive from '../../common/DialogContentAdaptive';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';
import StyledDialogActions from '../../common/StyledDialogActions';

export interface PostgresConnectionDetail {
    clientAddr: string | null;
    usename: string | null;
    datname: string | null;
    state: string | null;
    connections: number;
}

const AdminPostgresConnectionsDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const axios = useAxios();

    // Fetch the current Postgres connections grouped by client address, user, database and state
    const fetchPostgresConnections = async (): Promise<PostgresConnectionDetail[]> => {
        const query: string = `${settingsContext.routes.system.postgresConnections}`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return [];
            });
    };

    const { data: connections, isLoading } = useQuery(['postgresConnections'], () => fetchPostgresConnections(), {
        refetchOnWindowFocus: false,
        retry: false,
        refetchOnMount: true,
        refetchInterval: 1000 * 30, // 30 seconds
        enabled: open && userContext.jwtToken && settingsContext.apiKey && userContext.isAdmin ? true : false,
    });

    return (
        <DialogAdaptive
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth={true}
            scroll="paper"
            aria-labelledby="postgres-connections-dialog-title"
        >
            <StyledCardTitleBar title="Postgres Connections" isLoading={isLoading} />
            <DialogContentAdaptive>
                {!isLoading && (!connections || connections.length === 0) ? (
                    <Typography variant="body2">No connections found.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Client Address</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Database</TableCell>
                                    <TableCell>State</TableCell>
                                    <TableCell align="right">Connections</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(connections || []).map((connection, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{connection.clientAddr || 'local'}</TableCell>
                                        <TableCell>{connection.usename || '-'}</TableCell>
                                        <TableCell>{connection.datname || '-'}</TableCell>
                                        <TableCell>{connection.state || '-'}</TableCell>
                                        <TableCell align="right">{connection.connections.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContentAdaptive>
            <StyledDialogActions>
                <Button onClick={onClose}>Close</Button>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default AdminPostgresConnectionsDialog;
